import React from "react";
var __jsx = React.createElement;
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { UnstyledButton } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { Button } from "@/components/button";
import { showLegacyErrorToast } from "@/components/toast";
import { useSelectDecision } from "@/hooks/tasks";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { DecisionList } from "./decision-list";
import { getDecisionErrorMessage } from "./get-decision-error-message";
/** Step 1 card — presents AI-generated decision options for the user to confirm or override. */
export function SuggestedDecision(_ref) {
  var _suggestion$decisions, _recommendedDecision$;
  var suggestion = _ref.suggestion,
    confirmedDecisionId = _ref.confirmedDecisionId,
    isOverridden = _ref.isOverridden,
    patientUid = _ref.patientUid,
    reminderId = _ref.reminderId,
    onConfirm = _ref.onConfirm,
    onChangeDecision = _ref.onChangeDecision,
    onOverride = _ref.onOverride;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useSelectDecision = useSelectDecision(patientUid, reminderId),
    select = _useSelectDecision.mutate,
    isSelecting = _useSelectDecision.isLoading;
  var recommendedDecision = (_suggestion$decisions = suggestion.decisions.find(function (d) {
    return d.recommended;
  })) !== null && _suggestion$decisions !== void 0 ? _suggestion$decisions : suggestion.decisions[0];
  var _useState = useState((_recommendedDecision$ = recommendedDecision === null || recommendedDecision === void 0 ? void 0 : recommendedDecision.id) !== null && _recommendedDecision$ !== void 0 ? _recommendedDecision$ : null),
    selectedDecisionId = _useState[0],
    setSelectedDecisionId = _useState[1];
  var _useState2 = useState(false),
    showSelectionError = _useState2[0],
    setShowSelectionError = _useState2[1];
  var handleSelectDecision = function handleSelectDecision(id) {
    setShowSelectionError(false);
    setSelectedDecisionId(id);
  };

  // const hasMissingFields = suggestion.missing_fields.length > 0;
  var confirmedDecision = suggestion.decisions.find(function (d) {
    return d.id === confirmedDecisionId;
  });
  var isConfirmed = confirmedDecision !== undefined;
  var isSummary = isConfirmed || isOverridden;
  useEffect(function () {
    if (isConfirmed && confirmedDecisionId != null) {
      setSelectedDecisionId(confirmedDecisionId);
      return;
    }
    var selectionValid = suggestion.decisions.some(function (d) {
      return d.id === selectedDecisionId;
    });
    if (!selectionValid) {
      var _recommendedDecision$2;
      setSelectedDecisionId((_recommendedDecision$2 = recommendedDecision === null || recommendedDecision === void 0 ? void 0 : recommendedDecision.id) !== null && _recommendedDecision$2 !== void 0 ? _recommendedDecision$2 : null);
      setShowSelectionError(false);
    }
  }, [isConfirmed, confirmedDecisionId, selectedDecisionId, recommendedDecision === null || recommendedDecision === void 0 ? void 0 : recommendedDecision.id, suggestion.decisions]);
  return /*#__PURE__*/React.createElement("div", {
    className: "rounded-sds-300 border border-sds-base-default bg-sds-base-default p-4 flex flex-col gap-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex items-center gap-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ".concat(isSummary ? "bg-sds-success-default" : "bg-sds-brand-default")
  }, isSummary ? /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faCheck,
    className: "w-3 h-3 text-sds-success-on-success"
  }) : /*#__PURE__*/React.createElement("span", {
    className: "text-style-label-strong text-sds-brand-on-brand"
  }, "1")), /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col gap-0.5 flex-1 min-w-0"
  }, /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small-strong text-sds-base-default"
  }, t("label.suggested_decision", {
    defaultValue: "Suggested decision"
  })), isConfirmed ? /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small text-sds-base-secondary truncate"
  }, confirmedDecision.title) : isOverridden ? /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small text-sds-base-secondary truncate"
  }, t("label.overridden_choose_actions", {
    defaultValue: "Overridden — choose your own actions"
  })) : /*#__PURE__*/React.createElement("span", {
    className: "text-style-body-small text-md text-sds-base-secondary"
  }, t("label.suggested_decision_helper", {
    defaultValue: "Confirm AIRA's recommendation or choose another option."
  }))), isSummary && /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "suggested-decision-change",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.SUGGESTED_DECISION_CHANGE);
      onChangeDecision();
    },
    className: "text-style-body-small-strong text-sds-brand-default hover:text-sds-brand-secondary flex-shrink-0 transition-colors"
  }, t("button.change", {
    defaultValue: "Change"
  }))), !isSummary && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DecisionList, {
    decisions: suggestion.decisions,
    selectedDecisionId: selectedDecisionId,
    onSelectDecision: handleSelectDecision
  }), showSelectionError && /*#__PURE__*/React.createElement("p", {
    "data-testid": "suggested-decision-error",
    role: "alert",
    className: "text-style-body-small text-sds-urgent-default"
  }, t("error.select_decision_required", {
    defaultValue: "Please select a decision before confirming."
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-between"
  }, /*#__PURE__*/React.createElement(Button, {
    "data-testid": "suggested-decision-confirm",
    intent: "brand",
    variant: "primary",
    loading: isSelecting,
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.SUGGESTED_DECISION_CONFIRM);
      if (selectedDecisionId === null) {
        setShowSelectionError(true);
        return;
      }
      select({
        decision_id: selectedDecisionId,
        override: false
      }, {
        onSuccess: function onSuccess() {
          return onConfirm(selectedDecisionId);
        },
        onError: function onError(err) {
          showLegacyErrorToast(getDecisionErrorMessage(err));
        }
      });
    }
  }, t("button.confirm_decision", {
    defaultValue: "Confirm decision"
  })), /*#__PURE__*/React.createElement(UnstyledButton, {
    "data-testid": "suggested-decision-override",
    disabled: isSelecting,
    className: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sds-200 text-style-body-small-strong text-sds-brand-default hover:bg-sds-brand-tertiary active:bg-sds-brand-secondary transition-colors disabled:opacity-50",
    onClick: function onClick() {
      trackEvent(ANALYTICS_EVENTS.SUGGESTED_DECISION_OVERRIDE);
      select({
        decision_id: null,
        override: true
      }, {
        onSuccess: function onSuccess() {
          return onOverride();
        },
        onError: function onError(err) {
          showLegacyErrorToast(getDecisionErrorMessage(err));
        }
      });
    }
  }, t("button.override_and_choose_actions", {
    defaultValue: "Override & choose actions"
  }), /*#__PURE__*/React.createElement(FontAwesomeIcon, {
    icon: faArrowRight,
    className: "w-3 h-3"
  })))));
}